/**
 * Language and direction, for the builder's own interface.
 *
 * ⚠️ THE LANGUAGE OF THE *INTERFACE* IS NOT THE LANGUAGE OF THE *SITE BEING EDITED*, and conflating
 * them is the mistake this file exists to make impossible. A customer may build an
 * Arabic-and-English site while working in English, or the reverse. So there are two independent
 * choices:
 *
 *   - `useLang()` — what language THIS APPLICATION speaks. Persisted per person.
 *   - the editor's own preview-language toggle — which language of the CUSTOMER'S SITE is being
 *     previewed. That one lives in the editor, is per-site, and is passed to `@ksa/render` directly.
 *
 * The predecessor had one toggle doing both jobs, which meant "show me my Arabic homepage" also
 * translated the toolbar out from under somebody who does not read Arabic.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { dirFor, resolve, type Lang, type Text } from '@ksa/model'

const KEY = 'ksa:lang'

type LangCtx = {
  lang: Lang
  dir: 'ltr' | 'rtl'
  setLang: (lang: Lang) => void
  /** Read a bilingual value. */
  r: (value: Text) => string
  /** Inline literal pair, for copy that lives in a component rather than in a document. */
  t: (en: string, ar: string) => string
}

const Ctx = createContext<LangCtx | null>(null)

/**
 * ⚠️ THE DEFAULT IS ARABIC, AND THAT IS A PRODUCT DECISION RATHER THAN A CONSTANT. This is a Saudi
 * product; the majority of the people it is for read Arabic first. Defaulting to English and
 * offering Arabic as an option is what a product built elsewhere and localised afterwards looks
 * like, and it is visible from the first screen.
 *
 * The browser's own preference wins over the default, and a stored choice wins over both.
 */
function initialLang(): Lang {
  if (typeof window === 'undefined') return 'ar'
  try {
    const stored = window.localStorage.getItem(KEY)
    if (stored === 'en' || stored === 'ar') return stored
  } catch {
    /* storage disabled — fall through to the browser's preference */
  }
  const preferred = typeof navigator !== 'undefined' ? navigator.languages ?? [] : []
  for (const tag of preferred) {
    if (tag.toLowerCase().startsWith('ar')) return 'ar'
    if (tag.toLowerCase().startsWith('en')) return 'en'
  }
  return 'ar'
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang)

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    try {
      window.localStorage.setItem(KEY, next)
    } catch {
      /* a choice that cannot be remembered still applies for this session */
    }
  }, [])

  // ⚠️ `dir` AND `lang` GO ON `<html>`, not on a wrapper div. The direction of a scrollbar, of a
  // native `<select>` popup, and of the browser's own find-in-page all come from the document
  // element; setting it on a wrapper mirrors the content and leaves the chrome around it in the
  // other direction.
  useEffect(() => {
    const root = document.documentElement
    root.lang = lang
    root.dir = dirFor(lang)
  }, [lang])

  const value = useMemo<LangCtx>(
    () => ({
      lang,
      dir: dirFor(lang),
      setLang,
      r: (v: Text) => resolve(v, lang),
      t: (en: string, ar: string) => (lang === 'ar' ? ar : en),
    }),
    [lang, setLang],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useLang(): LangCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('@ksa/ui: useLang outside <LangProvider>')
  return ctx
}

/** Render a bilingual value. */
export function T({ value }: { value: Text }) {
  const { r } = useLang()
  return <>{r(value)}</>
}

/**
 * One button that names the language you are NOT in.
 *
 * > ⚠️ **THIS WAS TWO BUTTONS, EACH NAMING ITS OWN LANGUAGE — "العربية", not "Arabic" — AND THE
 * > REASONING FOR THAT IS KEPT BECAUSE IT IS STILL RIGHT.** Somebody who cannot read the current
 * > interface must recognise their own language in the control that changes it, so the label is
 * > still the target language written in itself, and it is still a visible control rather than a
 * > dropdown that hides the escape hatch behind a click in a language you cannot read. What changed
 * > is only that there is ONE of them.
 *
 * ⚠️ IT WAS TWO BUTTONS, AND ON A PHONE THAT COST A WHOLE ROW OF THE HEADER. Measured at 360 px:
 * the pair was 174 px, and with "My sites" (91) and "Sign out" (93) beside it the bar needed 374 px
 * of 328 available — so the header wrapped to three rows and stood 146 px tall, a fifth of the
 * screen, sticky, on every route. One button is 84 px and the bar is two rows.
 *
 * ⚠️ AND THE PAIR WAS ANSWERING A QUESTION NOBODY ASKS. `aria-pressed` on two buttons tells you which
 * language is active — which the entire page has already told you, in the language it is written in.
 * What somebody actually wants is the switch, so the control is the switch: while you are reading
 * English it says «العربية», and pressing it gives you Arabic. It is what every bilingual site does.
 *
 * The accessible name carries both languages, because a screen-reader user arriving on the Arabic
 * page should not have to guess what an English word in a nav bar does — and `lang` on the button
 * makes the label pronounce correctly rather than as English letters read aloud in Arabic.
 */
export function LangToggle({ className }: { className?: string }) {
  const { lang, setLang } = useLang()
  const next = lang === 'en' ? 'ar' : 'en'

  return (
    <button
      type="button"
      lang={next}
      className={`u-btn u-btn--ghost u-btn--sm ${className ?? ''}`}
      aria-label={next === 'ar' ? 'العربية — switch to Arabic' : 'English — التبديل إلى الإنجليزية'}
      onClick={() => setLang(next)}
    >
      {/*
        ⚠️ TWO CHARACTERS, NOT A WORD. «العربية» is 77 px and `English` 85; `ع` and `EN` are about
        30. In a header that also carries a site menu and a sign-out link on a 360 px phone, the
        difference is whether anything fits. The accessible name above still says it in full, so
        nothing is lost to somebody who cannot see the pill — only to the pixels.
      */}
      {next === 'ar' ? 'ع' : 'EN'}
    </button>
  )
}
